import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Input, Checkbox, Select, Tag, Button } from "antd";
import { CaretRightOutlined, FolderOutlined } from "@ant-design/icons";
import { observer } from "mobx-react";
import classNames from "classnames";
import BaseModal from "@app/components/modal";
import Loader from "@app/components/loader/content";
import notify from "@app/components/notify";

import chatStore from "../../state/store/ai-chat";
import {
    searchTokens,
    groupDocuments,
    filterGroups,
    uniqueDocuments,
    highlightChunks,
} from "./source-docs-groups";

import "./style/source-docs.scoped.scss";

const SEARCH_DELAY = 150;

const plural = (count, word) => `${count} ${word}${count !== 1 ? "s" : ""}`;

const Highlighted = ({ text, tokens }) => {
    return highlightChunks(text, tokens).map((chunk, index) =>
        chunk.match ? <mark key={index}>{chunk.text}</mark> : chunk.text,
    );
};

/**
 * Checkbox state of a set of documents
 */
const checkState = (documents, selected) => {
    const count = documents.filter((doc) => selected[doc._id]).length;

    return {
        checked: count > 0 && count === documents.length,
        indeterminate: count > 0 && count < documents.length,
    };
};

const DocumentRow = React.memo(({ file, groupId, checked, tokens, categoryNames, onToggle }) => {
    const others = file.categories.filter((id) => id !== groupId);

    return (
        <div className="document" onClick={() => onToggle(file._id)}>
            <div className="checkbox">
                <Checkbox
                    checked={checked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggle(file._id)}
                />
            </div>
            <div className="details">
                <div className="name" title={file.name}>
                    <Highlighted text={file.name} tokens={tokens} />
                </div>
                {others.length > 0 && (
                    <div className="tags">
                        {others.map((id) => (
                            <Tag key={id} title={`Also in: ${categoryNames[id]}`}>
                                <Highlighted text={categoryNames[id]} tokens={tokens} />
                            </Tag>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
});

const SourceDocsPicker = observer(({ files, onCancel, onSelect }) => {
    const [documents, setDocuments] = useState(null);
    const [categories, setCategories] = useState([]);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("");
    const [categoryIds, setCategoryIds] = useState([]);
    const [selected, setSelected] = useState({});
    const [collapsed, setCollapsed] = useState({});

    const tokens = useMemo(() => searchTokens(filter), [filter]);
    const groups = useMemo(() => groupDocuments(documents ?? [], categories), [
        documents,
        categories,
    ]);
    const categoryNames = useMemo(() => {
        const names = {};
        groups.forEach((group) => (names[group._id] = group.name));
        return names;
    }, [groups]);
    const visibleGroups = useMemo(() => filterGroups(groups, { tokens, categoryIds }), [
        groups,
        tokens,
        categoryIds,
    ]);
    const visible = useMemo(() => uniqueDocuments(visibleGroups), [visibleGroups]);
    const isFiltered = tokens.length > 0 || categoryIds.length > 0;
    const selectedCount = (documents ?? []).filter((doc) => selected[doc._id]).length;
    const selectAllState = checkState(visible, selected);
    const allCollapsed =
        visibleGroups.length > 0 && visibleGroups.every((group) => collapsed[group._id]);

    useEffect(() => {
        const selected = {};

        files.map((file) => {
            selected[file._id] = true;
        });

        setSelected({ ...selected });
    }, [documents]);

    useEffect(() => {
        const timer = setTimeout(() => setFilter(query), SEARCH_DELAY);
        return () => clearTimeout(timer);
    }, [query]);

    // show the matches expanded
    useEffect(() => {
        setCollapsed({});
    }, [tokens, categoryIds]);

    function toggleDocuments(list, value) {
        const newSelected = { ...selected };

        list.forEach((file) => {
            newSelected[file._id] = value;
        });

        setSelected(newSelected);
    }

    function onSelectAllToggle() {
        toggleDocuments(visible, !selectAllState.checked);
    }

    function onGroupToggle(group) {
        toggleDocuments(group.documents, !checkState(group.documents, selected).checked);
    }

    function onCollapseToggle(groupId) {
        setCollapsed({ ...collapsed, [groupId]: !collapsed[groupId] });
    }

    function onCollapseAllToggle() {
        const value = {};
        visibleGroups.forEach((group) => (value[group._id] = !allCollapsed));
        setCollapsed(value);
    }

    /**
     * Build the list of usable documents
     */
    async function load() {
        const [documents, categories] = await Promise.all([
            chatStore.loadSourceDocs(),
            chatStore.loadSourceDocCategories().catch(() => {
                notify.error("Error loading the document categories");
                return [];
            }),
        ]);
        const usable = documents
            .map((doc) => {
                const fileId = doc.getSourceFileId;
                if (fileId) {
                    return {
                        _id: fileId,
                        documentId: doc._id,
                        name: doc.name,
                        categories: doc.categories ?? [],
                    };
                }
            })
            .filter((doc) => !!doc);

        setCategories(categories);
        setDocuments(usable);
    }

    /**
     * Handle selection toggle
     */
    const onToggle = useCallback((fileId) => {
        setSelected((selected) => ({ ...selected, [fileId]: !selected[fileId] }));
    }, []);

    /**
     * Convert the selection object into a list of selected files
     */
    function handleSelection() {
        const files = documents
            .filter((doc) => selected[doc._id])
            .map((doc) => ({
                _id: doc._id,
                documentId: doc.documentId,
                name: doc.name,
                type: "file",
            }));

        onSelect(files);
    }

    useEffect(() => {
        load();
    }, []);

    function renderGroup(group) {
        const isCollapsed = !!collapsed[group._id];
        const state = checkState(group.documents, selected);

        return (
            <div className="group" key={group._id}>
                <div
                    className={classNames("group-header", { collapsed: isCollapsed })}
                    onClick={() => onCollapseToggle(group._id)}
                >
                    <CaretRightOutlined className="caret" />
                    <div className="checkbox">
                        <Checkbox
                            checked={state.checked}
                            indeterminate={state.indeterminate}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => onGroupToggle(group)}
                        />
                    </div>
                    <FolderOutlined className="folder" />
                    <div className="group-name" title={group.name}>
                        <Highlighted text={group.name} tokens={tokens} />
                    </div>
                    <div className="count">{group.documents.length}</div>
                </div>
                {!isCollapsed && (
                    <div className="group-documents">
                        {group.documents.map((file) => (
                            <DocumentRow
                                key={file._id}
                                file={file}
                                groupId={group._id}
                                checked={!!selected[file._id]}
                                tokens={tokens}
                                categoryNames={categoryNames}
                                onToggle={onToggle}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <BaseModal
            title="Pick Source Documents"
            visible={true}
            onCancel={onCancel}
            onOk={handleSelection}
            okText="Select"
            width={680}
            height="50vh"
            className={classNames("source-docs-picker")}
        >
            <div className="container">
                {documents && (
                    <>
                        <div className="search">
                            <Input.Search
                                placeholder="Search by file name or category"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onSearch={(value) => {
                                    setQuery(value);
                                    setFilter(value);
                                }}
                                allowClear
                            />
                            <Select
                                className="category-filter"
                                mode="multiple"
                                allowClear
                                showArrow
                                maxTagCount={1}
                                maxTagTextLength={18}
                                placeholder="All categories"
                                value={categoryIds}
                                onChange={setCategoryIds}
                                optionFilterProp="label"
                                options={groups.map((group) => ({
                                    value: group._id,
                                    label: `${group.name} (${group.documents.length})`,
                                }))}
                            />
                        </div>

                        {visible.length > 0 && (
                            <div className="select-all" onClick={onSelectAllToggle}>
                                <div className="checkbox">
                                    <Checkbox
                                        checked={selectAllState.checked}
                                        indeterminate={selectAllState.indeterminate}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={onSelectAllToggle}
                                    />
                                </div>
                                <div className="label">
                                    {isFiltered ? "Select All Matching" : "Select All"} (
                                    {plural(visible.length, "document")})
                                </div>
                                <div className="summary">{selectedCount} selected</div>
                                <Button
                                    className="collapse-all"
                                    type="link"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onCollapseAllToggle();
                                    }}
                                >
                                    {allCollapsed ? "Expand all" : "Collapse all"}
                                </Button>
                            </div>
                        )}

                        <div className="files">
                            {visibleGroups.map(renderGroup)}

                            {visible.length === 0 && (
                                <div className="empty">
                                    {isFiltered
                                        ? "No documents match your search."
                                        : "No source documents available."}
                                </div>
                            )}
                        </div>
                    </>
                )}

                {!documents && (
                    <div className="loading">
                        <Loader loading={true} />
                    </div>
                )}
            </div>
        </BaseModal>
    );
});

export default SourceDocsPicker;
